/**
 * UpcomingBillsCard
 *
 * Dashboard widget surfacing upcoming and overdue bills.
 * Improvements in [T38P0]:
 * • Total pending amount shown in the header.
 * • Overdue bills visually distinct (red row tint + icon).
 * • "Due today" state handled explicitly.
 * • Empty state with informative copy.
 * • Category colour dots for quick scanning.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Calendar, AlertCircle, CheckCircle2, AlarmClock } from "lucide-react";
import { Link } from "react-router-dom";
import { Bill } from "@/lib/types";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { useCurrency } from "@/hooks/useCurrency";

interface UpcomingBillsCardProps {
  bills: Bill[];
}

const categoryColorMap: Record<string, string> = {
  rent: "bg-blue-500",
  utilities: "bg-yellow-500",
  insurance: "bg-purple-500",
  internet: "bg-cyan-500",
  subscriptions: "bg-pink-500",
  mortgage: "bg-orange-500",
};

function getCategoryDot(category?: string): string {
  if (!category) return "bg-gray-400";
  return categoryColorMap[category.toLowerCase()] ?? "bg-gray-400";
}

function DueBadge({ daysUntil }: { daysUntil: number }) {
  if (daysUntil < 0) {
    return (
      <Badge
        variant="destructive"
        className="flex items-center gap-1 text-[10px] px-1.5 py-0 h-4"
      >
        <AlertCircle className="h-3 w-3" />
        Overdue
      </Badge>
    );
  }
  if (daysUntil === 0) {
    return (
      <Badge
        className="flex items-center gap-1 text-[10px] px-1.5 py-0 h-4
          bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 border-0"
      >
        <AlarmClock className="h-3 w-3" />
        Due today
      </Badge>
    );
  }
  if (daysUntil <= 3) {
    return (
      <Badge
        className="flex items-center gap-1 text-[10px] px-1.5 py-0 h-4
          bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 border-0"
      >
        <Calendar className="h-3 w-3" />
        {daysUntil}d
      </Badge>
    );
  }
  if (daysUntil <= 7) {
    return (
      <Badge
        variant="secondary"
        className="flex items-center gap-1 text-[10px] px-1.5 py-0 h-4"
      >
        <Calendar className="h-3 w-3" />
        {daysUntil}d
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="flex items-center gap-1 text-[10px] px-1.5 py-0 h-4 text-muted-foreground"
    >
      <Calendar className="h-3 w-3" />
      {daysUntil}d
    </Badge>
  );
}

export default function UpcomingBillsCard({ bills }: UpcomingBillsCardProps) {
  const today = new Date();
  const { fmt } = useCurrency();

  const upcomingBills = bills
    .filter((b) => b.status === "upcoming")
    .sort((a, b) => {
      const dA = differenceInCalendarDays(parseISO(a.dueDate), today);
      const dB = differenceInCalendarDays(parseISO(b.dueDate), today);
      return dA - dB;
    })
    .slice(0, 5);

  const overdueCount = upcomingBills.filter(
    (b) => differenceInCalendarDays(parseISO(b.dueDate), today) < 0
  ).length;

  const totalDue = upcomingBills.reduce((s, b) => s + b.amount, 0);

  return (
    <Card
      data-section="upcoming-bills"
      data-testid="home-card-list-upcoming"
      className="h-full transition-all duration-200 hover:shadow-md hover:border-primary/20 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-full">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Upcoming Bills</CardTitle>
              {overdueCount > 0 && (
                <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {overdueCount} overdue
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-primary"
            asChild
          >
            <Link to="/bills" className="flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </>
      </CardHeader>
      <CardContent>
      {upcomingBills.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/60 mb-3">
              <CheckCircle2 className="h-7 w-7 text-muted-foreground" />
            </div>
            <h4 className="text-sm font-semibold text-foreground mb-1">
              All caught up!
            </h4>
            <p className="text-xs text-muted-foreground max-w-[180px]">
              No pending bills at the moment.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {/* Total due line */}
            {totalDue > 0 && (
              <p className="text-xs text-muted-foreground mb-3">
                Total pending:{" "}
                <span className="font-semibold text-foreground">
                  {fmt(totalDue)}
                </span>
              </p>
            )}

            {upcomingBills.map((bill) => {
              const daysUntil = differenceInCalendarDays(
                parseISO(bill.dueDate),
                today
              );
              const isOverdue = daysUntil < 0;

              return (
                <div
                  key={bill._id}
                  className={`grid grid-cols-[32px_1fr_auto] gap-3 items-center
                    rounded-lg py-2.5 px-2 -mx-2 transition-colors
                    ${
                      isOverdue
                        ? "bg-red-50/60 dark:bg-red-900/10"
                        : "hover:bg-muted/40"
                    }`}
                >
                  {/* Category colour dot */}
                  <div className="flex items-center justify-center">
                    <span
                      className={`h-2 w-2 rounded-full ${getCategoryDot(
                        bill.category
                      )}`}
                    />
                  </div>

                  {/* Name + due date + badge */}
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-tight truncate">
                      {bill.name}
                    </p>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(bill.dueDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <DueBadge daysUntil={daysUntil} />
                    </div>
                  </div>

                  {/* Amount */}
                  <p
                    className={`text-sm font-semibold tabular-nums shrink-0 ${
                      isOverdue
                        ? "text-red-600 dark:text-red-400"
                        : "text-foreground"
                    }`}
                  >
                    {fmt(bill.amount)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

